import { ArtPipeline } from "../../../art";
import { dropfrontCatalog } from "../../../data/dropfront-catalog";
import type { DropfrontCardId } from "../../../data/dropfront-catalog";
import type { DropfrontState, HudSnapshot } from "../../../features/dropfront";
import { DfPhase } from "../../../features/dropfront/dropfront-events";
import { t } from "../../../data/strings";
import hudPresetTable from "../../../data/tables/ui/huds/dropfront.json";
import { getUiLayout } from "../../layout";
import type { HudShellHandle } from "../../layout/hud-shell";
import type { UiHudPreset } from "../../layout/types";
import { dockHtml, resultHtml, topHtml } from "./markup";

export type DropfrontHudHandlers = {
  onSelectCard: (id: DropfrontCardId) => void;
  onPauseToggle: () => void;
  onRestart: () => void;
  onQuit: () => void;
};

const preset = hudPresetTable as UiHudPreset;
const ART_PACK = "dropfront";

export function mountDropfrontHud(handlers: DropfrontHudHandlers): {
  sync: (state: DropfrontState, snap: HudSnapshot, toast?: string) => void;
  destroy: () => void;
} {
  const ui = getUiLayout();
  ui.sync();
  const shell: HudShellHandle = ui.mountShell(preset);

  const top = shell.region("top");
  top.style.pointerEvents = "auto";
  top.innerHTML = topHtml();

  const mid = shell.region("mid");
  mid.style.pointerEvents = "none";
  mid.innerHTML = "";

  const bottom = shell.region("bottom");
  bottom.style.pointerEvents = "auto";
  bottom.innerHTML = dockHtml();

  // Card silhouette thumbs (baked once)
  for (const c of dropfrontCatalog.cards) {
    const img = bottom.querySelector(
      `[data-card="${c.id}"] .df-card-thumb`,
    ) as HTMLImageElement | null;
    if (!img) continue;
    try {
      img.src = ArtPipeline.spriteDataUrl(ART_PACK, c.spriteId, "ally", 2);
      img.alt = t(c.nameKey);
    } catch {
      img.hidden = true;
    }
  }

  const modal = shell.region("modal");
  modal.hidden = true;
  modal.style.pointerEvents = "auto";
  /** Only rebuild result DOM when entering Won/Lost — avoid killing in-flight clicks. */
  let lastModalPhase: string | null = null;

  top.querySelector("[data-pause]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onPauseToggle();
  });
  top.querySelector("[data-restart]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onRestart();
  });
  top.querySelector("[data-quit]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onQuit();
  });

  for (const c of dropfrontCatalog.cards) {
    bottom.querySelector(`[data-card="${c.id}"]`)?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onSelectCard(c.id);
    });
  }

  const bindModal = () => {
    modal.querySelector("[data-restart]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onRestart();
    });
    modal.querySelector("[data-quit]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onQuit();
    });
  };

  return {
    sync: (state, snap, toast) => {
      ui.sync();
      const supplyEl = top.querySelector("[data-supply]");
      if (supplyEl) {
        supplyEl.textContent = t("df.fmt.supply", {
          label: t("df.supply"),
          n: snap.supply,
        });
      }
      const hqFill = top.querySelector("[data-hq-fill]") as HTMLElement | null;
      if (hqFill) {
        const r = snap.hqMax > 0 ? snap.hqHp / snap.hqMax : 0;
        hqFill.style.width = `${Math.max(0, Math.min(100, r * 100))}%`;
      }
      const hqText = top.querySelector("[data-hq]");
      if (hqText) {
        hqText.textContent = t("df.fmt.hq", {
          label: t("df.hq"),
          hp: Math.ceil(snap.hqHp),
          max: Math.ceil(snap.hqMax),
        });
      }
      const msg = top.querySelector("[data-msg]");
      if (msg) {
        msg.textContent =
          toast ||
          (snap.phase === DfPhase.Paused
            ? t("df.msg.paused")
            : snap.selectedCard
              ? t("df.msg.tapBoard")
              : t("df.msg.hint"));
      }

      for (const c of dropfrontCatalog.cards) {
        const btn = bottom.querySelector(`[data-card="${c.id}"]`) as HTMLButtonElement | null;
        if (!btn) continue;
        btn.classList.toggle("df-card-selected", snap.selectedCard === c.id);
        btn.disabled = !snap.cardAfford[c.id] && snap.selectedCard !== c.id;
        btn.classList.toggle("df-card-disabled", !snap.cardAfford[c.id]);
      }

      const resultPhase =
        snap.phase === DfPhase.Won || snap.phase === DfPhase.Lost
          ? snap.phase
          : null;
      if (resultPhase) {
        modal.hidden = false;
        if (lastModalPhase !== resultPhase) {
          lastModalPhase = resultPhase;
          modal.innerHTML = resultHtml(
            snap.phase === DfPhase.Won,
            snap.supply,
            state.dropsUsed,
          );
          bindModal();
        }
      } else {
        if (lastModalPhase !== null) {
          lastModalPhase = null;
          modal.hidden = true;
          modal.innerHTML = "";
        } else {
          modal.hidden = true;
        }
      }
    },
    destroy: () => {
      shell.destroy();
    },
  };
}
